import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useDiagnosticDepartments,
  useDiagnosticDepartment,
  useUpdateDiagnosticDepartment,
  useDeleteDiagnosticDepartment,
  useBulkDeleteDiagnosticDepartments,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { DiagnosticDepartmentModal } from './DiagnosticDepartmentModal';
import { toast } from 'sonner';
import {
  Building2,
  Loader2,
  AlertCircle,
  Plus,
  Pencil,
  Search,
  X,
  CheckCircle,
  XCircle,
  Edit3,
  Trash2,
  ToggleRight,
  ToggleLeft,
  Globe,
} from 'lucide-react';

export function DiagnosticDepartments() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [viewModal, setViewModal] = useState<{ open: boolean; id: string } | null>(null);
  const [formModal, setFormModal] = useState<{ id?: string } | null>(null);

  const { data, isLoading, error } = useDiagnosticDepartments(page, limit, debouncedSearch || undefined, true);
  const { data: viewData, isLoading: viewLoading, error: viewError } = useDiagnosticDepartment(
    viewModal?.open ? viewModal.id : undefined
  );
  const updateDepartment = useUpdateDiagnosticDepartment();
  const deleteDepartment = useDeleteDiagnosticDepartment();
  const bulkDeleteDepartments = useBulkDeleteDiagnosticDepartments();
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const departments = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;
  const trashedCount = Number(data?.stats?.trashed ?? 0);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(departments.map((dept: any) => dept.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [departments]);

  const allSelected = departments.length > 0 && departments.every((dept: any) => selectedIds.has(dept.id));
  const someSelected = departments.some((dept: any) => selectedIds.has(dept.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = departments.length > 0 && departments.every((dept: any) => prev.has(dept.id));
      return everySelected ? new Set() : new Set(departments.map((dept: any) => dept.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      await bulkDeleteDepartments.mutateAsync(ids);
      setSelectedIds(new Set());
      toast.success(`Deleted ${ids.length} department${ids.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Failed to delete some departments');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDepartment.mutateAsync(deleteTarget.id);
      toast.success('Department deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete department');
    }
  };

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load diagnostic departments</span>
      </div>
    );
  }

  const handleToggleStatus = async () => {
    if (!viewModal?.id || !viewData?.data) return;
    setTogglingStatus(true);
    try {
      await updateDepartment.mutateAsync({
        id: viewModal.id,
        data: { status: viewData.data.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
      });
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleToggleVisibility = async () => {
    if (!viewModal?.id || !viewData?.data) return;
    setTogglingVisibility(true);
    try {
      await updateDepartment.mutateAsync({
        id: viewModal.id,
        data: { showOnWebsite: !viewData.data.showOnWebsite },
      });
    } finally {
      setTogglingVisibility(false);
    }
  };

  const department = viewData?.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">Departments</h1>
            <p className="text-sm text-text-muted">
              Single source of truth for hospital departments and diagnostic configuration.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/departments/trash')}
            className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            title="View trash"
          >
            <Trash2 size={16} />
            Trash
            {trashedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none">
                {trashedCount > 99 ? '99+' : trashedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setFormModal({})}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <Plus size={16} />
            Add Department
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search departments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => { setSearch(''); setPage(1); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteDepartments.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteDepartments.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteDepartments.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="px-4 py-3 w-10 text-left">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={departments.length === 0}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    title="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {departments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-text-muted">
                    {search ? (
                      <span>No departments match your search.{' '}
                        <button onClick={() => { setSearch(''); setPage(1); }} className="text-primary hover:underline">
                          Clear search
                        </button>
                      </span>
                    ) : (
                      'No departments found'
                    )}
                  </td>
                </tr>
              ) : (
                departments.map((dept: any) => (
                  <tr key={dept.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(dept.id)}
                        onChange={() => toggleOne(dept.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setViewModal({ open: true, id: dept.id })}
                        className="font-medium text-text hover:text-primary transition-colors text-left cursor-pointer"
                      >
                        {dept.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-mono text-text">{dept.code}</td>
                    <td className="px-4 py-3 text-text-muted max-w-xs truncate">{dept.description || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        dept.status === 'ACTIVE' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                      }`}>
                        {dept.status === 'ACTIVE' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                        {dept.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setFormModal({ id: dept.id })}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget({ id: dept.id, name: dept.name })}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="departments"
        />
      </div>

      {/* View Modal */}
      {viewModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 p-4 bg-surface border-b border-border">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                  <Building2 className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-text truncate">
                    {department?.name || 'Department Details'}
                  </h2>
                  {department && (
                    <p className="text-xs font-mono text-text-muted truncate">{department.code}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setViewModal(null)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            {viewLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : viewError || !department ? (
              <div className="flex items-center gap-2 m-4 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
                <AlertCircle size={16} className="shrink-0" />
                <span>Failed to load department details. Please try again.</span>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {/* Key facts */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-background rounded-lg border border-border p-3">
                    <p className="text-xs text-text-muted">Display Order</p>
                    <p className="text-sm font-medium text-text mt-0.5">{department.displayOrder ?? '—'}</p>
                  </div>
                  <div className="bg-background rounded-lg border border-border p-3">
                    <p className="text-xs text-text-muted">Status</p>
                    <span
                      className={`inline-flex items-center gap-1 text-sm font-medium mt-0.5 ${
                        department.status === 'ACTIVE' ? 'text-success' : 'text-danger'
                      }`}
                    >
                      {department.status === 'ACTIVE' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {department.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {department.description && (
                  <div className="bg-background rounded-lg border border-border p-3">
                    <p className="text-xs text-text-muted">Description</p>
                    <p className="text-sm text-text mt-0.5">{department.description}</p>
                  </div>
                )}

                {/* Status toggle */}
                <button
                  onClick={handleToggleStatus}
                  disabled={togglingStatus || updateDepartment.isPending}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 ${
                    department.status === 'ACTIVE'
                      ? 'bg-success/10 text-success border-success/20 hover:bg-success/20'
                      : 'bg-danger/10 text-danger border-danger/20 hover:bg-danger/20'
                  }`}
                >
                  {togglingStatus || updateDepartment.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : department.status === 'ACTIVE' ? (
                    <XCircle size={16} />
                  ) : (
                    <CheckCircle size={16} />
                  )}
                  {department.status === 'ACTIVE' ? 'Deactivate Department' : 'Activate Department'}
                </button>

                {/* Website visibility toggle */}
                <button
                  onClick={handleToggleVisibility}
                  disabled={togglingVisibility || updateDepartment.isPending}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 ${
                    department.showOnWebsite
                      ? 'bg-success/10 text-success border-success/20 hover:bg-success/20'
                      : 'bg-danger/10 text-danger border-danger/20 hover:bg-danger/20'
                  }`}
                >
                  {togglingVisibility || updateDepartment.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : department.showOnWebsite ? (
                    <ToggleRight size={16} />
                  ) : (
                    <ToggleLeft size={16} />
                  )}
                  {department.showOnWebsite ? 'Shown on Website' : 'Hidden from Website'}
                </button>
                <p className="text-xs text-text-muted flex items-center gap-1.5">
                  <Globe size={13} className="text-primary shrink-0" />
                  {department.showOnWebsite
                    ? 'This department appears on the public website / frontend.'
                    : 'This department is hidden from the public website / frontend.'}
                </p>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    onClick={() => setViewModal(null)}
                    className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text border border-border rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      const editId = department.id;
                      setViewModal(null);
                      setFormModal({ id: editId });
                    }}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
                  >
                    <Edit3 size={16} />
                    Edit
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {formModal && (
        <DiagnosticDepartmentModal
          departmentId={formModal.id}
          onClose={() => setFormModal(null)}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete department?"
        description={
          <>
            This will move <strong className="text-text">{deleteTarget?.name}</strong> to the trash.
            You can restore it from the trash later.
          </>
        }
        confirmLabel="Delete department"
        loading={deleteDepartment.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'}?`}
        description="This action will remove the selected departments. This cannot be undone."
        confirmLabel={`Delete ${selectedIds.size} department${selectedIds.size === 1 ? '' : 's'}`}
        loading={bulkDeleteDepartments.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
